/**
 * Downloading every file of a run as one archive.
 *
 * The API decides and records first, then streams: it returns a short-lived link, and
 * following it saves the ZIP without the browser ever holding a delivery in memory. The
 * reason a PII project demands is asked once for the whole archive rather than per file.
 */

import { useState } from "react";

import { ApiError } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { Notice } from "./ui";

function readableSize(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${value} B` : `${value.toFixed(1)} ${units[unit]}`;
}

export function DownloadAllButton({
  projectSlug,
  runId,
  fileCount,
  totalBytes,
  needsReason,
  kind,
  onBundled,
}: {
  projectSlug: string;
  runId: string;
  fileCount: number;
  totalBytes: number;
  needsReason: boolean;
  /** When set, only files of this kind are included. */
  kind?: "output" | "input";
  /** Called once a bundle has been asked for, so the file list picks it up. */
  onBundled: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [excluded, setExcluded] = useState<string[]>([]);
  const [bundling, setBundling] = useState(false);
  const [bundled, setBundled] = useState(false);

  /**
   * Build it once and keep it, rather than streaming it on every download.
   *
   * Worth it for a large delivery: the archive lands in S3 beside the run's other files
   * and is then downloaded directly, instead of occupying the API for the length of
   * every transfer.
   */
  const bundle = async (withReason?: string) => {
    setBundling(true);
    setProblem(undefined);
    try {
      const started = await endpoints.bundleArtifacts(projectSlug, runId, withReason, kind);
      setExcluded(started.excluded);
      setBundled(true);
      onBundled();
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
      if (caught instanceof ApiError && caught.status === 422) setAsking(true);
    } finally {
      setBundling(false);
    }
  };

  const start = async (withReason?: string) => {
    setBusy(true);
    setProblem(undefined);
    setExcluded([]);
    try {
      const grant = await endpoints.prepareArchive(projectSlug, runId, withReason, kind);
      if (grant.excluded.length > 0) setExcluded(grant.excluded);
      // POST the grant token via a hidden form so the browser saves the ZIP without
      // the token ever appearing in a URL (and therefore in access logs or Nginx's
      // URI-size limit).
      const streamUrl = endpoints.streamArchiveUrl(projectSlug, runId);
      const form = document.createElement("form");
      form.method = "POST";
      form.action = streamUrl;
      form.style.display = "none";
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "grant";
      input.value = grant.token;
      form.appendChild(input);
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      setAsking(false);
      setReason("");
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
      if (caught instanceof ApiError && caught.status === 422) setAsking(true);
    } finally {
      setBusy(false);
    }
  };

  if (asking) {
    return (
      <div className="stack" style={{ gap: 6, minWidth: 280 }}>
        <input
          className="input"
          placeholder={`Why do you need these ${fileCount} files?`}
          value={reason}
          autoFocus
          onChange={(event) => setReason(event.target.value)}
        />
        {problem && <div className="field__error">{problem}</div>}
        <div className="row">
          <button
            type="button"
            className="button button--small button--primary"
            disabled={busy || reason.trim().length < 10}
            onClick={() => start(reason)}
          >
            Download all
          </button>
          <button
            type="button"
            className="button button--small"
            disabled={bundling || reason.trim().length < 10}
            onClick={() => bundle(reason)}
          >
            Bundle
          </button>
          <button
            type="button"
            className="button button--small button--ghost"
            onClick={() => setAsking(false)}
          >
            Cancel
          </button>
        </div>
        <div className="faint" style={{ fontSize: 12 }}>
          At least 10 characters. Recorded against your name, once for all {fileCount} files.
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <button
        type="button"
        className="button button--small"
        disabled={busy}
        onClick={() => (needsReason ? setAsking(true) : start())}
      >
        {busy ? "Preparing…" : `Download all (${fileCount}) · ${readableSize(totalBytes)}`}
      </button>
      <button
        type="button"
        className="button button--small button--ghost"
        disabled={bundling}
        title="Build a ZIP once and keep it beside the run's other files"
        onClick={() => (needsReason ? setAsking(true) : bundle())}
      >
        {bundling ? "Bundling…" : "Bundle"}
      </button>
      {bundled && (
        <Notice tone="info">
          Building the archive. It appears in this list when it is ready — large
          deliveries take a few minutes.
        </Notice>
      )}
      {excluded.length > 0 && (
        <Notice tone="warning">
          {excluded.length} file(s) were left out because they failed validation:{" "}
          {excluded.join(", ")}. Download them individually to release them.
        </Notice>
      )}
      {problem && <Notice tone="danger">{problem}</Notice>}
    </div>
  );
}
